import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  doc, 
  getDoc,
  serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from './firebaseConfig';
import { getFunctions } from 'firebase/functions';
import staffAuthManager from './staffAuthService';

const functions = getFunctions();

class AttendanceManager {
  constructor() {
    this.currentUser = null;
    this.warehouseLocations = [];
    this.validationRules = {
      maxPhotoSize: 5 * 1024 * 1024, // 5MB
      requiredGPSAccuracy: 100, // meters
      duplicateTimeWindow: 15 * 60 * 1000 // 15 minutes
    };
  }

  // Submit attendance record
  async submitAttendance(attendanceData) {
    try {
      const currentEmployee = staffAuthManager.getCurrentEmployee();
      if (!currentEmployee) {
        throw new Error('Employee not authenticated');
      }

      // Validate attendance data
      const validation = this.validateAttendanceData(attendanceData);
      if (!validation.isValid) {
        throw new Error(`Validation failed: ${validation.errors.join(', ')}`);
      }

      // Prepare data for Cloud Function
      const submissionData = {
        attendanceData: {
          type: attendanceData.type,
          location: {
            latitude: attendanceData.location.latitude,
            longitude: attendanceData.location.longitude,
            accuracy: attendanceData.location.accuracy,
            altitude: attendanceData.location.altitude,
            timestamp: attendanceData.location.timestamp
          },
          photoData: attendanceData.photoUrl, // Base64 data URL
          photoMetadata: {
            captureTime: attendanceData.photoMetadata.captureTime,
            deviceInfo: attendanceData.photoMetadata.deviceInfo,
            isLiveCapture: attendanceData.photoMetadata.isLiveCapture,
            fileSize: attendanceData.photoMetadata.fileSize,
            mimeType: attendanceData.photoMetadata.mimeType
          },
          notes: attendanceData.notes || '',
          warehouseProximity: attendanceData.warehouseProximity
        }
      };

      // Call Cloud Function
      const submitAttendanceFunction = httpsCallable(functions, 'submitAttendance');
      const result = await submitAttendanceFunction(submissionData);

      return {
        success: true,
        attendanceId: result.data.attendanceId,
        status: result.data.status,
        validationResults: result.data.validationResults,
        message: result.data.message
      };

    } catch (error) {
      console.error('Submit attendance error:', error);
      return {
        success: false,
        error: error.message || 'Failed to submit attendance'
      };
    }
  }

  // Get attendance history for current employee
  async getAttendanceHistory(startDate = null, endDate = null, limitCount = 50) {
    try {
      const currentEmployee = staffAuthManager.getCurrentEmployee();
      if (!currentEmployee) {
        throw new Error('Employee not authenticated');
      }

      let attendanceQuery = query(
        collection(db, 'attendance'),
        where('uid', '==', currentEmployee.uid),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      );

      // Add date filters if provided
      if (startDate) {
        attendanceQuery = query(
          attendanceQuery,
          where('timestamp', '>=', startDate)
        );
      }

      if (endDate) {
        attendanceQuery = query(
          attendanceQuery,
          where('timestamp', '<=', endDate)
        );
      }

      const snapshot = await getDocs(attendanceQuery);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null
      }));

      return {
        success: true,
        records: records,
        count: records.length
      };

    } catch (error) {
      console.error('Get attendance history error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get attendance history',
        records: []
      };
    }
  }

  // Get today's attendance status
  async getTodayAttendanceStatus() {
    try {
      const currentEmployee = staffAuthManager.getCurrentEmployee();
      if (!currentEmployee) {
        throw new Error('Employee not authenticated');
      }

      // Get today's date range
      const today = new Date();
      const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);

      const todayQuery = query(
        collection(db, 'attendance'),
        where('uid', '==', currentEmployee.uid),
        where('timestamp', '>=', startOfDay),
        where('timestamp', '<=', endOfDay),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(todayQuery);
      const todayRecords = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null
      }));

      // Determine current status
      let status = 'not-clocked-in';
      let lastRecord = null;
      let clockInTime = null;
      let clockOutTime = null;

      if (todayRecords.length > 0) {
        lastRecord = todayRecords[0];
        
        // Find last clock-in and clock-out
        const clockInRecord = todayRecords.find(r => r.type === 'clock-in');
        const clockOutRecord = todayRecords.find(r => r.type === 'clock-out');

        if (clockInRecord) {
          clockInTime = clockInRecord.timestamp;
        }

        if (clockOutRecord) {
          clockOutTime = clockOutRecord.timestamp;
        }

        // Determine status based on last record
        if (lastRecord.type === 'clock-in') {
          status = 'clocked-in';
        } else if (lastRecord.type === 'clock-out') {
          status = 'clocked-out';
        }
      }

      // Calculate total hours worked today
      let hoursWorked = 0;
      if (clockInTime && clockOutTime) {
        hoursWorked = (clockOutTime - clockInTime) / (1000 * 60 * 60); // Convert to hours
      } else if (clockInTime && status === 'clocked-in') {
        hoursWorked = (new Date() - clockInTime) / (1000 * 60 * 60); // Current working hours
      }

      return {
        success: true,
        status: status,
        lastRecord: lastRecord,
        clockInTime: clockInTime,
        clockOutTime: clockOutTime,
        hoursWorked: Math.round(hoursWorked * 100) / 100, // Round to 2 decimal places
        todayRecords: todayRecords
      };

    } catch (error) {
      console.error('Get today attendance status error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get today\'s attendance status',
        status: 'unknown'
      };
    }
  }

  // Get attendance statistics
  async getAttendanceStats(startDate, endDate) {
    try {
      const currentEmployee = staffAuthManager.getCurrentEmployee();
      if (!currentEmployee) {
        throw new Error('Employee not authenticated');
      }

      const statsQuery = query(
        collection(db, 'attendance'),
        where('uid', '==', currentEmployee.uid),
        where('timestamp', '>=', startDate),
        where('timestamp', '<=', endDate),
        orderBy('timestamp', 'desc')
      );

      const snapshot = await getDocs(statsQuery);
      const records = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        timestamp: doc.data().timestamp?.toDate() || null
      }));

      // Calculate statistics
      const stats = {
        totalRecords: records.length,
        clockIns: records.filter(r => r.type === 'clock-in').length,
        clockOuts: records.filter(r => r.type === 'clock-out').length,
        approvedRecords: records.filter(r => r.status === 'approved').length,
        pendingRecords: records.filter(r => r.status === 'pending').length,
        rejectedRecords: records.filter(r => r.status === 'rejected').length,
        warehouseAttendance: records.filter(r => r.isWarehouse).length,
        offSiteAttendance: records.filter(r => !r.isWarehouse).length,
        averageValidationScore: records.length > 0 
          ? records.reduce((sum, r) => sum + (r.validationResults?.overallScore || 0), 0) / records.length 
          : 0
      };

      // Calculate total hours worked
      let totalHours = 0;
      const dailyRecords = {};

      // Group records by date
      records.forEach(record => {
        const dateKey = record.timestamp.toDateString();
        if (!dailyRecords[dateKey]) {
          dailyRecords[dateKey] = [];
        }
        dailyRecords[dateKey].push(record);
      });

      // Calculate hours for each day
      Object.keys(dailyRecords).forEach(dateKey => {
        const dayRecords = dailyRecords[dateKey].sort((a, b) => a.timestamp - b.timestamp);
        let dayHours = 0;
        let lastClockIn = null;

        dayRecords.forEach(record => {
          if (record.type === 'clock-in') {
            lastClockIn = record.timestamp;
          } else if (record.type === 'clock-out' && lastClockIn) {
            dayHours += (record.timestamp - lastClockIn) / (1000 * 60 * 60);
            lastClockIn = null;
          }
        });

        totalHours += dayHours;
      });

      stats.totalHours = Math.round(totalHours * 100) / 100;
      stats.averageHoursPerDay = stats.totalHours / Object.keys(dailyRecords).length || 0;

      return {
        success: true,
        stats: stats,
        records: records
      };

    } catch (error) {
      console.error('Get attendance stats error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get attendance statistics'
      };
    }
  }

  // Validate attendance data before submission
  validateAttendanceData(attendanceData) {
    const errors = [];

    // Check required fields
    if (!attendanceData.type || !['clock-in', 'clock-out'].includes(attendanceData.type)) {
      errors.push('Invalid attendance type');
    }

    if (!attendanceData.location) {
      errors.push('Location data is required');
    } else {
      if (!attendanceData.location.latitude || !attendanceData.location.longitude) {
        errors.push('GPS coordinates are required');
      }
      if (!attendanceData.location.accuracy || attendanceData.location.accuracy > this.validationRules.requiredGPSAccuracy) {
        errors.push(`GPS accuracy must be better than ${this.validationRules.requiredGPSAccuracy} meters`);
      }
    }

    if (!attendanceData.photoUrl) {
      errors.push('Photo is required');
    } else {
      // Check photo size (approximate from base64)
      const photoSize = attendanceData.photoUrl.length * 0.75; // Base64 is ~33% larger
      if (photoSize > this.validationRules.maxPhotoSize) {
        errors.push('Photo size exceeds maximum allowed size');
      }
    }

    if (!attendanceData.photoMetadata) {
      errors.push('Photo metadata is required');
    } else {
      if (!attendanceData.photoMetadata.captureTime) {
        errors.push('Photo capture time is required');
      }
      if (!attendanceData.photoMetadata.isLiveCapture) {
        errors.push('Only live captured photos are allowed');
      }
    }

    return {
      isValid: errors.length === 0,
      errors: errors
    };
  }

  // Get warehouse locations
  async getWarehouseLocations() {
    try {
      const warehouseQuery = query(
        collection(db, 'warehouseLocations'),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(warehouseQuery);
      const locations = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      this.warehouseLocations = locations;

      return {
        success: true,
        locations: locations
      };

    } catch (error) {
      console.error('Get warehouse locations error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get warehouse locations',
        locations: []
      };
    }
  }

  // Get single attendance record
  async getAttendanceRecord(recordId) {
    try {
      const recordDoc = await getDoc(doc(db, 'attendance', recordId));
      
      if (!recordDoc.exists()) {
        throw new Error('Attendance record not found');
      }

      const recordData = {
        id: recordDoc.id,
        ...recordDoc.data(),
        timestamp: recordDoc.data().timestamp?.toDate() || null
      };

      return {
        success: true,
        record: recordData
      };

    } catch (error) {
      console.error('Get attendance record error:', error);
      return {
        success: false,
        error: error.message || 'Failed to get attendance record'
      };
    }
  }

  // Set current user
  setCurrentUser(user) {
    this.currentUser = user;
  }

  // Get validation rules
  getValidationRules() {
    return this.validationRules;
  }
}

// Create singleton instance
const attendanceManager = new AttendanceManager();

export default attendanceManager;
export { AttendanceManager };
